import { EmeraldButton } from "@emerald-react/button";
import useSurveyState from "@hooks/useSurveyState";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useResetPasscodeMutation } from "@services/survey-core";
import Analytics from "@shared/utils/tagManagerAnalytics";
import { useState } from "react";

const ShareWebLinkSection = () => {
  const { survey } = useSurveyState();
  const [isCopyingWebLink, setIsCopyingWebLink] = useState(false);
  const [resetPasscode, { isLoading: isResetting }] = useResetPasscodeMutation();
  const { confirmAction } = useSimpleConfirmation();

  // Generate link inline
  const generatedLink = (() => {
    if (!survey?.id) return "";
    const baseUrl =
      import.meta.env.VITE_INTERACTIVE_WEB_APP_ROOT_URL ||
      window.location.origin;
    return `${baseUrl}/interactive/${survey.id}/login`;
  })();

  // Generate copy text inline
  const copyText = (() => {
    if (!generatedLink) return "";
    return survey?.passcode
      ? `Property Web Survey: ${generatedLink}\nPasscode for secure access: ${survey.passcode}`
      : `Property Web Survey: ${generatedLink}`;
  })();

  const handleCopyLink = () => {
    if (!copyText || !survey) return;

    // Find section indices safely
    const marketMapIndex = survey.sections?.findIndex(s => s.name?.toLowerCase().includes('marketmap')) ?? -1;
    const propertyComparisonIndex = survey.sections?.findIndex(s => s.name?.toLowerCase().includes('comparison')) ?? -1;
    const propertyIndex = survey.sections?.findIndex(s => s.name?.toLowerCase().includes('properties')) ?? -1;
    const tourScheduleIndex = survey.sections?.findIndex(s => s.name?.toLowerCase().includes('tour')) ?? -1;
    
    // Check if properties have custom fields
    const hasCustomFields = survey.properties?.some(property => 
      property.customFields && property.customFields.length > 0
    ) ?? false;
    
    // Get active property list
    const activePropertyList = survey.properties?.filter(property => !property.isHidden).length ?? 0;

    Analytics.send({
      event: 'surveyCopyLink',
      SurveyID: survey.id,
      SurveyName: survey.name,
      Cover: survey.sections?.[0] ? !survey.sections[0].isHidden : false,
      BackgroundImage: survey.coverBackgroundImage?.displayName || '',
      SurveyTitle: survey.coverTitle || '',
      SurveySubtitle: survey.coverSubTitle || '',
      ClientName: survey.clientName || '',
      MarketMap: marketMapIndex >= 0 ? !survey.sections[marketMapIndex].isHidden : false,
      PropertyComparison: propertyComparisonIndex >= 0 ? !survey.sections[propertyComparisonIndex].isHidden : false,
      Properties: propertyIndex >= 0 ? !survey.sections[propertyIndex].isHidden : false,
      TourSchedule: tourScheduleIndex >= 0 ? !survey.sections[tourScheduleIndex].isHidden : false,
      NumberOfProperties: survey.properties?.length || 0,
      PropertyCustomFields: hasCustomFields,
      ProjectID: survey.projectId || '',
      TeamID: survey.teamId || '',
      SurveyCreationDate: survey.createdOn || '',
      Origin: survey.origin || '',
      ProjectCreationDate: survey.projectCreatedAt || '',
      SiteIQUserID: survey.createdBy || '', // Use createdBy instead of user.userIdList
      CustomeLegends: survey.customLegends || '',
      ShortList: survey.isClientShortlist || false,
      Collaborator: survey.isCollaborator || false,
      SubjectProperties: activePropertyList,
    });

    setIsCopyingWebLink(true);
    navigator.clipboard.writeText(copyText);

    // Show feedback for 1.5 seconds
    setTimeout(() => {
      setIsCopyingWebLink(false);
    }, 1500);
  };

  const handleGenerateNewPassword = async () => {
    if (!survey?.id) return;

    const confirmed = await confirmAction(
      "Generate New Web Survey Password?",
      "Are you sure you want to create a new Survey password? Click Yes to continue or Cancel to keep the existing password",
      {
        icon: "warning",
        confirmButtonText: "Yes",
        confirmButtonColor: "#dc3545",
        cancelButtonText: "Cancel",
      },
    );

    if (!confirmed) return;
    await resetPasscode(survey.id);
  };

  // Generate button label inline — show current passcode when available
  const passcodeDisplay = survey?.passcode ? ` (${survey.passcode})` : "";
  const buttonLabel = isCopyingWebLink ? "Copied" : `Copy Web Survey link${passcodeDisplay}`;

  return (
    <div className="flex flex-col gap-y-3">
      <EmeraldButton
        label={buttonLabel}
        onClick={handleCopyLink}
        className="w-full"
        disabled={isCopyingWebLink}
      />
      <p className="text-sm text-gray-900">
        Copy link for interactive password-protected website. The interactive
        website is always up to date. If you need to change the password click
        the link below. People with the current password will not be able to
        access the survey.
      </p>
      <EmeraldButton
        label={isResetting ? "Generating..." : "Generate New Web Survey Password"}
        onClick={handleGenerateNewPassword}
        className="w-full"
        disabled={isResetting || !survey?.id}
        variant="Secondary"
      />
    </div>
  );
};

export default ShareWebLinkSection;
